/*
 * @Author: gaobin
 * @Date: 2024-08-10 15:21:05
 * @LastEditors: Please set LastEditors
 * @LastEditTime: 2025-07-15 15:10:35
 * @Description: 处理数据方法集合
 */
import failIcon from '@/static/images/common/fail.png';
import successIcon from '@/static/images/common/success.png';
import warningIcon from '@/static/images/common/warning.png';
import { ENUM_JUMP_TYPE } from '@utils/constant';

import store from '@/store';

/*
 * 判断obj是否为一个整数
 */
export function isInteger(obj) {
  return Math.floor(obj) === obj;
}

/*
 * 将一个浮点数转成整数，返回整数和倍数。如 3.14 >> 314，倍数是 100
 * @param floatNum {number} 小数
 * @return {object}
 * {times:100, num: 314}
 */
export function toInteger(floatNum) {
  const ret = { times: 1, num: 0 };
  if (isInteger(floatNum)) {
    ret.num = floatNum;
    return ret;
  }
  const strfi = `${floatNum}`;
  const dotPos = strfi.indexOf('.');
  const len = strfi.substr(dotPos + 1).length;
  const times = Math.pow(10, len);
  const intNum = parseInt(floatNum * times + 0.5, 10);
  ret.times = times;
  ret.num = intNum;
  return ret;
}

/*
 * 核心方法，实现加减乘除运算，确保不丢失精度
 * 思路：把小数放大为整数（乘），进行算术运算，再缩小为小数（除）
 *
 * @param a {number} 运算数1
 * @param b {number} 运算数2
 * @param op {string} 运算类型，有加减乘除（add/subtract/multiply/divide）
 *
 */
export function fourRuleOperation(a, b, op = 'multiply') {
  const o1 = toInteger(a);
  const o2 = toInteger(b);
  const n1 = o1.num;
  const n2 = o2.num;
  const t1 = o1.times;
  const t2 = o2.times;
  const max = t1 > t2 ? t1 : t2;
  let result = null;
  switch (op) {
    case 'add':
      if (t1 === t2) {
        // 两个小数位数相同
        result = n1 + n2;
      } else if (t1 > t2) {
        // o1 小数位 大于 o2
        result = n1 + n2 * (t1 / t2);
      } else {
        // o1 小数位 小于 o2
        result = n1 * (t2 / t1) + n2;
      }
      return result / max;
    case 'subtract':
      if (t1 === t2) {
        result = n1 - n2;
      } else if (t1 > t2) {
        result = n1 - n2 * (t1 / t2);
      } else {
        result = n1 * (t2 / t1) - n2;
      }
      return result / max;
    case 'multiply':
      result = (n1 * n2) / (t1 * t2);
      return result;
    // divide
    default:
      result = (n1 / n2) * (t2 / t1);
      return result;
  }
}

// 处理sku数据
export function arrangeSkuList(arr = [], lang = 'zh_CN') {
  // console.log('arr instanceof Array', arr instanceof Array);
  const hadleArr = Array.isArray(arr) ? arr : [];
  const attrImgInfo = {};
  const attrCombineInfo = {};
  const skuObj = {};
  let initSkuData;
  // 如果商品都没库存
  if (hadleArr.findIndex((item) => item.skuStock) === -1) {
    initSkuData = Object.assign({}, hadleArr[0]);
  } else {
    // 设置有库存的为默认选中
    initSkuData = Object.assign({}, hadleArr.filter((item) => item.skuStock)[0]);
  }
  const attrNameKey = lang === 'zh_CN' ? 'attributeName' : 'attributeNameTrans';
  const attrValueKey = lang === 'zh_CN' ? 'value' : 'valueTrans';
  const skuPriceKey = 'promotionPrice';
  for (const sku of hadleArr) {
    const attributes = Array.isArray(sku.attributes) ? sku.attributes : [];
    if (sku.attributes) {
      sku.properties = sku.attributes;
    }
    // 获取sku渲染列表
    for (let i = 0; i < attributes.length; i++) {
      const item = attributes[i];
      const key = `_${i}_${item[attrNameKey]}`;
      const val = item[attrValueKey];
      if (!skuObj[key]) {
        skuObj[key] = [];
      }
      if (skuObj[key].some((item1) => item1.attrValue === val)) continue;

      if (item.skuImageUrl) attrImgInfo[val] = item.skuImageUrl;

      skuObj[key].push({
        skuImageUrl: item.skuImageUrl,
        attrValue: val,
      });
    }
    // console.log('attributes', attributes);
    // 获取sku组合对象
    // 这里只把 attributes.length - 1 个sku进行组合
    const attrKey1 = attributes.slice(0, attributes.length - 1).reduce((prev, cur, index) => {
      return prev ? `${prev}.${cur[attrValueKey]}` : prev + cur[attrValueKey];
    }, '');

    const attrKey2 = attributes.slice(-1).reduce((prev, cur, index) => {
      return prev + cur[attrValueKey];
    }, '');
    // 其它规格组合
    if (!attrCombineInfo[attrKey1]) {
      attrCombineInfo[attrKey1] = {};
    }
    // 操作数量的规格
    attrCombineInfo[attrKey1][attrKey2] = Object.assign(
      {},
      sku,
      { attributes: null },
      attributes[attributes.length - 1],
      { name: attrKey2 },
    );

    // 获取组合价格
    const skuPrice = sku[skuPriceKey];
    // console.log('initSkuData', initSkuData)
    const { minPrice, maxPrice, skuImageUrl } = initSkuData;
    if (!minPrice || skuPrice < minPrice) {
      initSkuData.minPrice = skuPrice;
    }
    if (!maxPrice || skuPrice > maxPrice) {
      initSkuData.maxPrice = skuPrice;
    }
    if (!skuImageUrl && attributes[0]) {
      initSkuData = Object.assign(initSkuData, attributes[0]);
    }
  }
  const attrMap = Object.keys(skuObj).map((item) => {
    return {
      attrName: item.replace(/^_\d_/g, ''),
      attrList: skuObj[item] || [],
    };
  });

  // 属性str
  initSkuData.checkedValue = attrMap.map((item) => item.attrName).join(' ');
  if (initSkuData.minPrice === initSkuData.maxPrice) {
    delete initSkuData.minPrice;
    delete initSkuData.maxPrice;
  }
  return {
    attrMap, // 属性Map
    attrCombineInfo, // 可选组合Info
    attrImgInfo, // 属性图片信息
    initSkuData, // 初始化属性信息
    notAttrMap: hadleArr.every((item) => !item.attributes),
    valid: !!hadleArr.length, // 是否有效的sku
  };
}

/*
 * 应用内页面跳转
 *
 * @param url {String} 跳转地址
 * @param type {number} 跳转类型 0-保留当前跳转 1-关闭当前跳转 2-关闭所有跳转 3-跳转到tabBar 4-外部链接
 *
 */
export function newPageJump(url, type) {
  switch (type) {
    case ENUM_JUMP_TYPE.TYPE_CLEAR_CURRENT_JUMP: {
      uni.redirectTo({
        url,
      });
      break;
    }
    case ENUM_JUMP_TYPE.TYPE_CLEAR_ALL_JUMP: {
      uni.reLaunch({
        url,
      });
      break;
    }
    case ENUM_JUMP_TYPE.TYPE_GO_TO_TABBAR_JUMP: {
      uni.switchTab({
        url,
      });
      break;
    }
    case ENUM_JUMP_TYPE.TYPE_KEEP_CURRENT_JUMP:
    default: {
      uni.navigateTo({
        url,
      });
    }
  }
}

// 判断是否是手机
export function isMobile() {
  // 获取用户代理字符串
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // 定义移动端关键词列表
  const mobileKeywords = [
    'Mobile',
    'Android',
    'iPhone',
    'iPad',
    'iPod',
    'BlackBerry',
    'IEMobile',
    'Opera Mini',
  ];

  // 检查用户代理字符串中是否包含任何移动端关键词
  for (let i = 0; i < mobileKeywords.length; i++) {
    if (userAgent.indexOf(mobileKeywords[i]) > -1) {
      return true;
    }
  }

  // 如果没有匹配到任何移动端关键词，则认为是PC端
  return false;
}

/*
 * 图片缩放处理
 *
 * @param { String } url 图片地址
 * @param {number} w 图片宽度
 * @param {number} h 图片高度
 *
 */
export function getZoomPictureUrl(url, w = 600, h = 600) {
  let newUrl = url;
  if (!url) {
    return '';
  }

  const httpType1Arr = ['cbu01.alicdn.com'];
  const httpType2Arr = [
    'oss-singapore.pandapon.com',
    'oss-shanghai.pandapon.com',
    'oss.uwebuy.com',
  ];

  const isSubstringExist1 = httpType1Arr.some((item) => url.includes(item));
  const isSubstringExist2 = httpType2Arr.some((item) => url.includes(item));

  if (isSubstringExist1) {
    const expandedName = url.split('.');
    newUrl = `${url}_${w}x${h}.${expandedName[expandedName.length - 1]}`;
  } else if (isSubstringExist2) {
    newUrl = `${url}?x-oss-process=image/resize,w_${w},h_${h}`;
  }
  return newUrl;
}

/*
 * 全局toast提示
 */
export function showToast(option) {
  const { title, type, duration, image } = option;
  let imageTemp = null;
  if (image) {
    imageTemp = image;
  } else {
    switch (type) {
      case 'success':
        imageTemp = successIcon;
        break;
      case 'fail':
        imageTemp = failIcon;
        break;
      case 'warning':
        imageTemp = warningIcon;
        break;
      default:
        imageTemp = null;
        break;
    }
  }
  const newOption = {
    title: title || '',
    icon: 'none',
    mask: true,
  };
  if (imageTemp) {
    newOption.iconWidth = '36';
    newOption.iconHeight = '36';
    newOption.image = imageTemp;
    newOption.icon = imageTemp;
  }

  // #ifdef H5
  uni.showToast({
    ...newOption,
    duration: duration || 3000,
  });
  // #endif

  // #ifdef APP-PLUS
  plus.nativeUI.toast(title, {
    ...newOption,
    duration: 'long',
    align: 'center',
    verticalAlign: 'center',
  });
  // #endif
}

export function getAreaFieldName(platform = '') {
  const { lang } = store.getters;
  switch (lang) {
    case 'ar':
      return platform === 'shareClub' ? 'areaEnName' : 'areaArName';
    case 'en':
      return 'areaEnName';
    default:
      return 'areaName';
  }
}

/*
 * 聊天头像处理
 *
 * @param { String } userName 用户名称
 */
export function handleUserAvatarName(userName) {}

/**
 * 压缩图片 - 简单可靠的实现
 * @param {*} filePath 图片路径或Blob对象
 * @param {*} options 压缩参数
 * @returns 返回压缩后的图片路径或Blob对象
 */
export const compressImage = (filePath, options) => {
  return new Promise((resolve) => {
    console.log(
      '开始压缩图片:',
      typeof filePath === 'string' ? `${filePath.substring(0, 50)}...` : '非字符串数据',
    );

    // 设置超时保护 - 无论如何，25秒后一定返回结果
    const timeoutId = setTimeout(() => {
      console.warn('图片压缩超时(25秒)，返回原图');
      resolve(filePath);
    }, 5000);

    // 文件大小阈值 - 5MB
    const SIZE_THRESHOLD = 5 * 100 * 1024;

    // 安全返回函数
    const safeResolve = (result) => {
      clearTimeout(timeoutId);
      console.log('压缩完成，返回结果');
      resolve(result);
    };

    try {
      // APP环境下的压缩
      // #ifdef APP-PLUS
      if (typeof filePath !== 'string') {
        // console.warn('APP环境下只能处理字符串路径');
        safeResolve(filePath);
        return;
      }

      // 先检查文件大小
      plus.io.resolveLocalFileSystemURL(
        filePath,
        (entry) => {
          entry.file(
            (file) => {
              // 如果文件小于5MB，直接返回原图
              if (file.size < SIZE_THRESHOLD) {
                console.log('APP文件小于阈值，不压缩:', file.size / 1024, 'KB');
                safeResolve(filePath);
                return;
              }

              // 否则开始压缩流程
              compressImageApp(filePath, options, 0);
            },
            (err) => {
              console.error('APP获取文件信息失败:', err);
              // 获取文件信息失败，尝试压缩
              compressImageApp(filePath, options, 0);
            },
          );
        },
        (err) => {
          console.error('APP解析文件路径失败:', err);
          // 解析文件路径失败，尝试压缩
          compressImageApp(filePath, options, 0);
        },
      );

      function compressImageApp(path, opts, attempt) {
        // 最多压缩3次
        if (attempt >= 3) {
          console.log('APP已达最大压缩次数(3)，返回当前结果');
          safeResolve(path);
          return;
        }

        // 计算当前尝试的压缩参数
        const quality = Math.max(70 - attempt * 30, 10); // 从80%递减，最低10%
        const width = Math.floor(1440 * Math.pow(0.7, attempt)); // 宽度也递减

        console.log(`APP第${attempt + 1}次压缩，质量=${quality}%, 宽度=${width}px`);

        uni.compressImage({
          src: path,
          quality,
          width: width.toString(),
          success: (res) => {
            if (!res.tempFilePath) {
              console.error('APP压缩失败，未获取到路径');
              safeResolve(path);
              return;
            }

            // 获取压缩后文件大小
            plus.io.resolveLocalFileSystemURL(
              res.tempFilePath,
              (entry) => {
                entry.file(
                  (file) => {
                    console.log(`APP第${attempt + 1}次压缩后大小:`, file.size / 1024, 'KB');

                    // 如果已经小于阈值，直接返回
                    if (file.size < SIZE_THRESHOLD) {
                      console.log('APP压缩后小于阈值，停止继续压缩');
                      safeResolve(res.tempFilePath);
                      return;
                    }

                    // 如果是最后一次尝试，直接返回结果
                    if (attempt >= 2) {
                      console.log('APP完成最后一次压缩，返回结果');
                      safeResolve(res.tempFilePath);
                      return;
                    }

                    // 继续下一次压缩
                    console.log('APP继续下一次压缩');
                    setTimeout(() => {
                      compressImageApp(res.tempFilePath, opts, attempt + 1);
                    }, 0);
                  },
                  (err) => {
                    console.error('APP获取压缩后文件信息失败:', err);
                    // 如果是最后一次尝试，直接返回结果
                    if (attempt >= 2) {
                      safeResolve(res.tempFilePath);
                    } else {
                      // 继续下一次压缩
                      setTimeout(() => {
                        compressImageApp(res.tempFilePath, opts, attempt + 1);
                      }, 0);
                    }
                  },
                );
              },
              (err) => {
                console.error('APP解析压缩后文件路径失败:', err);
                // 如果是最后一次尝试，直接返回结果
                if (attempt >= 2) {
                  safeResolve(res.tempFilePath);
                } else {
                  // 继续下一次压缩
                  setTimeout(() => {
                    compressImageApp(res.tempFilePath, opts, attempt + 1);
                  }, 0);
                }
              },
            );
          },
          fail: (err) => {
            console.error(`APP第${attempt + 1}次压缩失败:`, err);
            // 压缩失败，如果还有机会，尝试更低质量
            if (attempt < 2) {
              setTimeout(() => {
                compressImageApp(path, opts, attempt + 1);
              }, 0);
            } else {
              safeResolve(path); // 最后一次失败则返回原图
            }
          },
        });
      }
      // #endif

      // H5环境下的压缩
      // #ifdef H5
      // H5环境下处理Blob/File对象
      if (filePath instanceof Blob || filePath instanceof File) {
        // 检查文件大小
        if (filePath.size < SIZE_THRESHOLD) {
          console.log('H5文件小于阈值，不压缩:', filePath.size / 1024, 'KB');
          const objectUrl = URL.createObjectURL(filePath);
          setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
          safeResolve(objectUrl);
          return;
        }

        // 开始压缩流程
        compressImageH5(filePath, options, 0);
      }
      // H5环境下处理URL
      else if (typeof filePath === 'string') {
        // 处理各种URL类型
        if (filePath.startsWith('blob:')) {
          console.log('H5检测到blob:URL，获取内容后压缩');
          // 获取blob内容后进行压缩
          fetch(filePath)
            .then((response) => response.blob())
            .then((blob) => {
              if (blob.size < SIZE_THRESHOLD) {
                console.log('H5 blob内容小于阈值，不压缩:', blob.size / 1024, 'KB');
                // 这里使用原URL更好，因为已经是blob:URL
                safeResolve(filePath);
              } else {
                console.log('H5压缩blob内容:', blob.size / 1024, 'KB');
                compressImageH5(blob, options, 0);
              }
            })
            .catch((err) => {
              console.error('H5获取blob内容失败:', err);
              safeResolve(filePath);
            });
        }
        // 处理http/https URL
        else if (filePath.startsWith('http')) {
          console.log('H5检测到http URL，获取内容后压缩');
          fetch(filePath)
            .then((response) => response.blob())
            .then((blob) => {
              if (blob.size < SIZE_THRESHOLD) {
                console.log('H5远程图片小于阈值，不压缩:', blob.size / 1024, 'KB');
                // 创建一个新的blob URL并返回
                const objectUrl = URL.createObjectURL(blob);
                setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
                safeResolve(objectUrl);
              } else {
                console.log('H5压缩远程图片:', blob.size / 1024, 'KB');
                compressImageH5(blob, options, 0);
              }
            })
            .catch((err) => {
              console.error('H5获取远程图片失败:', err);
              safeResolve(filePath);
            });
        }
        // 本地路径
        else {
          console.log('H5处理本地路径');
          // 尝试获取Blob
          fetch(filePath)
            .then((response) => response.blob())
            .then((blob) => {
              if (blob.size < SIZE_THRESHOLD) {
                console.log('H5文件小于阈值，不压缩:', blob.size / 1024, 'KB');
                const objectUrl = URL.createObjectURL(blob);
                setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
                safeResolve(objectUrl);
              } else {
                compressImageH5(blob, options, 0);
              }
            })
            .catch((err) => {
              console.error('H5获取Blob失败:', err);
              safeResolve(filePath);
            });
        }
      }
      // 其他类型无法处理
      else {
        console.warn('H5环境下不支持的文件类型');
        safeResolve(filePath);
      }

      // H5环境压缩函数 - 使用canvas
      function compressImageH5(file, opts, attempt) {
        // 最多压缩3次
        if (attempt >= 3) {
          console.log('H5已达最大压缩次数(3)，返回当前结果');
          const objectUrl = URL.createObjectURL(file);
          // 创建的URL会在页面关闭时自动释放，但最好在适当的时候手动释放
          setTimeout(() => {
            try {
              URL.revokeObjectURL(objectUrl);
            } catch (e) {
              console.log('释放URL出错，可忽略', e);
            }
          }, 30000); // 假设30秒后图片已不再需要
          safeResolve(objectUrl);
          return;
        }

        console.log(`H5第${attempt + 1}次压缩，原始大小:`, file.size / 1024, 'KB');

        // 计算当前尝试的压缩参数 - 逐步降低质量和尺寸
        const quality = Math.max(0.6 - attempt * 0.25, 0.1); // 从70%递减，最低10%
        const maxWidth = Math.floor(1080 * Math.pow(0.7, attempt)); // 宽度也递减

        console.log(`H5压缩参数: 质量=${quality * 100}%, 宽度=${maxWidth}px`);

        try {
          const reader = new FileReader();

          reader.onerror = () => {
            console.error('H5读取文件失败');
            const objectUrl = URL.createObjectURL(file);
            setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
            safeResolve(objectUrl);
          };

          reader.onload = (e) => {
            const img = new Image();

            img.onerror = () => {
              console.error('H5加载图片失败');
              const objectUrl = URL.createObjectURL(file);
              setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
              safeResolve(objectUrl);
            };

            img.onload = () => {
              try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                // 计算尺寸
                let { width } = img;
                let { height } = img;

                if (width > maxWidth) {
                  height = Math.floor(height * (maxWidth / width));
                  width = maxWidth;
                }

                canvas.width = width;
                canvas.height = height;

                // 绘制图片
                ctx.fillStyle = '#fff';
                ctx.fillRect(0, 0, width, height);
                ctx.drawImage(img, 0, 0, width, height);

                // 转为Blob
                canvas.toBlob(
                  (blob) => {
                    if (!blob) {
                      console.error('H5转换Blob失败');
                      const objectUrl = URL.createObjectURL(file);
                      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
                      safeResolve(objectUrl);
                      return;
                    }

                    console.log(`H5第${attempt + 1}次压缩后大小:`, blob.size / 1024, 'KB');

                    // 如果已经小于阈值，直接返回
                    if (blob.size < SIZE_THRESHOLD) {
                      console.log('H5压缩后小于阈值，停止继续压缩');
                      const objectUrl = URL.createObjectURL(blob);
                      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
                      safeResolve(objectUrl);
                      return;
                    }

                    // 如果是最后一次尝试，直接返回结果
                    if (attempt >= 2) {
                      console.log('H5完成最后一次压缩，返回结果');
                      const objectUrl = URL.createObjectURL(blob);
                      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
                      safeResolve(objectUrl);
                      return;
                    }

                    // 继续下一次压缩
                    console.log('H5继续下一次压缩');
                    setTimeout(() => {
                      compressImageH5(blob, opts, attempt + 1);
                    }, 0);
                  },
                  file.type || 'image/jpeg',
                  quality,
                );
              } catch (err) {
                console.error('H5处理Canvas失败:', err);
                const objectUrl = URL.createObjectURL(file);
                setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
                safeResolve(objectUrl);
              }
            };

            img.src = e.target.result;
          };

          reader.readAsDataURL(file);
        } catch (err) {
          console.error('H5压缩过程中出现异常:', err);
          const objectUrl = URL.createObjectURL(file);
          setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
          safeResolve(objectUrl);
        }
      }
      // #endif
    } catch (err) {
      console.error('压缩图片过程中出现异常:', err);
      safeResolve(filePath); // 出错时返回原图
    }
  });
};

export function checkAndroidPermission(permission) {
  return new Promise((resolve, reject) => {
    if (plus.android) {
      plus.android.checkPermission(permission, (granted) => {
        if (granted.checkResult === 0) {
          resolve('authorized'); // 有权限
        } else {
          resolve('denied'); // 无权限
        }
      });
    } else {
      reject(new Error('null'));
    }
  });
}

/**
 * 检查并执行应用更新
 */
export const checkAndHandleUpdate = async () => {
  try {
    // 获取当前应用信息
    const widgetInfo = await getAppWidgetInfo();
    console.log('widgetInfo', widgetInfo);

    const { version: currentVersion, versionCode: currentVersionCode } = widgetInfo;

    // 获取服务器最新版本信息
    const versionRes = await request('/uwb-app/api/v1/home/getLatestWqt', { method: 'get' });
    if (versionRes.code !== 200) return;

    // 比较版本是否需要更新
    if (shouldUpdate(versionRes.data, currentVersion, currentVersionCode)) {
      await handleUpdate(versionRes.data);
    }
  } catch (error) {
    console.error('更新检查失败:', error);
  }
};

/**
 * 获取应用widget信息
 */
const getAppWidgetInfo = () => {
  return new Promise((resolve) => {
    plus.runtime.getProperty(plus.runtime.appid, resolve);
  });
};

/**
 * 判断是否需要更新
 */
const shouldUpdate = (serverData, currentVersion, currentVersionCode) => {
  const storedVersion = uni.getStorageSync('APP_VERSION');
  const storedVersionCode = uni.getStorageSync('APP_VERSION_CODE');

  // 使用存储的版本号优先比较，没有则使用当前版本
  const compareVersion = storedVersion || currentVersion;
  const compareVersionCode = storedVersionCode || currentVersionCode;

  return serverData.version !== compareVersion || serverData.versionCode !== compareVersionCode;
};

/**
 * 处理更新流程
 */
const handleUpdate = async (updateInfo) => {
  // const downloadRes = await proxy.$services.downLoadWgt({
  //   method: 'get',
  //   data: {
  //     version: updateInfo.version,
  //     versionCode: updateInfo.versionCode,
  //     groupName: 'wgt',
  //   },
  // });
  // if (downloadRes.code !== 200 || !downloadRes.data.url) return;
  // 根据isDownload决定更新方式
  updateInfo.isDownload ? await silentUpdate(updateInfo) : showUpdateDialog(updateInfo);
  // downloadRes.data.isDownload
  //   ? await silentUpdate(downloadRes.data)
  //   : showUpdateDialog(downloadRes.data);
};

/**
 * 静默更新
 */
const silentUpdate = async (downloadData) => {
  try {
    const tempFilePath = await downloadFile(downloadData.url);
    await installWgt(tempFilePath);
    // 更新本地存储的版本信息
    uni.setStorageSync('APP_VERSION', downloadData.version);
    uni.setStorageSync('APP_VERSION_CODE', downloadData.versionCode);
    console.log('静默更新完成');
  } catch (error) {
    console.error('静默更新失败:', error);
  }
};

/**
 * 显示更新弹窗
 */
const showUpdateDialog = (downloadData) => {
  uni.showModal({
    title: '版本更新提示',
    content: '当前有新版本，确定更新吗？',
    confirmText: '更新',
    cancelText: '取消',
    success: async (res) => {
      if (res.confirm) {
        try {
          const tempFilePath = await downloadFile(downloadData.url);
          await installWgt(tempFilePath, true); // 安装后重启
        } catch (error) {
          uni.showToast({ title: '更新失败', icon: 'none' });
        }
      }
    },
  });
};

/**
 * 下载文件封装
 */
const downloadFile = (url) => {
  return new Promise((resolve, reject) => {
    uni.downloadFile({
      url,
      success: (res) =>
        res.statusCode === 200 ? resolve(res.tempFilePath) : reject(new Error('下载失败')),
      fail: reject,
    });
  });
};

/**
 * 安装wgt文件
 */
const installWgt = (tempFilePath, shouldRestart = false) => {
  return new Promise((resolve, reject) => {
    plus.runtime.install(
      tempFilePath,
      {
        force: false,
      },
      () => {
        shouldRestart && plus.runtime.restart();
        resolve();
      },
      reject,
    );
  });
};
